import { useEffect, useRef, useState } from 'react';

interface AddressComponents {
  addressLine1: string;
  city: string;
  state: string;
  zip: string;
  country: string;
}

interface AddressAutocompleteProps {
  value: string;
  onChange: (value: string) => void;
  onSelect: (address: AddressComponents) => void;
  placeholder?: string;
  className?: string;
}

// ----------------------------------------------------------------------------
// Google Maps Places typing notes.
//
// The Next.js webapp had `@types/google.maps` installed as a devDependency,
// which provided the `google.maps.places.*` global types. We deliberately do
// not pull that 700KB type package into the jest-client just for one
// autocomplete widget. Instead we declare the minimum surface area inline,
// which keeps the component self-typed and prevents the global namespace
// pollution from leaking into the rest of the SPA.
// ----------------------------------------------------------------------------
interface PlaceAddressComponent {
  short_name: string;
  long_name?: string;
  types: string[];
}

interface PlaceResult {
  address_components?: PlaceAddressComponent[];
}

interface AutocompleteInstance {
  addListener(event: string, handler: () => void): void;
  getPlace(): PlaceResult;
}

interface AutocompleteOptions {
  types?: string[];
  componentRestrictions?: { country: string | string[] };
  fields?: string[];
}

// Minimal shape of `window.google.maps.places` we actually call.
interface GooglePlacesNamespace {
  Autocomplete: new (
    input: HTMLInputElement,
    options?: AutocompleteOptions
  ) => AutocompleteInstance;
}

interface GoogleMapsEventNamespace {
  clearInstanceListeners(target: object): void;
}

interface GoogleNamespace {
  maps: {
    places: GooglePlacesNamespace;
    event: GoogleMapsEventNamespace;
  };
}

declare global {
  interface Window {
    google?: GoogleNamespace;
  }
}

// Load Google Places script once globally
let scriptPromise: Promise<void> | null = null;
function loadGooglePlaces(): Promise<void> {
  if (scriptPromise) return scriptPromise;

  // Vite exposes build-time env vars under `import.meta.env` and requires
  // the `VITE_` prefix to bundle them into the client. The Next.js source
  // used `process.env.NEXT_PUBLIC_GOOGLE_MAPS_API_KEY`; the jest-client
  // expects `VITE_GOOGLE_MAPS_API_KEY` in its `.env` instead.
  const apiKey = import.meta.env.VITE_GOOGLE_MAPS_API_KEY as string | undefined;
  if (!apiKey) {
    return Promise.reject(new Error('No Google Maps API key'));
  }

  scriptPromise = new Promise((resolve, reject) => {
    if (typeof window !== 'undefined' && window.google?.maps?.places) {
      resolve();
      return;
    }
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${apiKey}&libraries=places`;
    script.async = true;
    script.onload = () => resolve();
    script.onerror = () => {
      scriptPromise = null;
      reject(new Error('Failed to load Google Maps'));
    };
    document.head.appendChild(script);
  });

  return scriptPromise;
}

function parsePlace(place: PlaceResult): AddressComponents {
  const components: Record<string, string> = {};
  for (const comp of place.address_components || []) {
    for (const type of comp.types) {
      components[type] = comp.short_name;
    }
  }

  const streetNumber = components['street_number'] || '';
  const route = components['route'] || '';
  const addressLine1 = streetNumber ? `${streetNumber} ${route}` : route;

  return {
    addressLine1: addressLine1 || '',
    city: components['locality'] || components['sublocality_level_1'] || components['administrative_area_level_3'] || '',
    state: components['administrative_area_level_1'] || '',
    zip: components['postal_code'] || '',
    country: components['country'] || '',
  };
}

/**
 * Returns true if a visible Google Places .pac-container exists in the DOM.
 * Used by parent modals to avoid dismissing when the dropdown is clicked.
 */
export function isPlacesDropdownVisible(): boolean {
  const pac = document.querySelector('.pac-container') as HTMLElement | null;
  return !!pac && pac.style.display !== 'none' && pac.offsetHeight > 0;
}

export function AddressAutocomplete({
  value,
  onChange,
  onSelect,
  placeholder,
  className,
}: AddressAutocompleteProps) {
  const inputRef = useRef<HTMLInputElement>(null);
  const autocompleteRef = useRef<AutocompleteInstance | null>(null);
  const onSelectRef = useRef(onSelect);
  const onChangeRef = useRef(onChange);
  const [loaded, setLoaded] = useState(false);

  onSelectRef.current = onSelect;
  onChangeRef.current = onChange;

  useEffect(() => {
    loadGooglePlaces()
      .then(() => setLoaded(true))
      .catch(() => {});
  }, []);

  useEffect(() => {
    if (!loaded || !inputRef.current || autocompleteRef.current) return;
    const places = window.google?.maps?.places;
    if (!places) return;

    const autocomplete = new places.Autocomplete(inputRef.current, {
      types: ['address'],
      componentRestrictions: { country: 'us' },
      fields: ['address_components'],
    });

    autocomplete.addListener('place_changed', () => {
      const place = autocomplete.getPlace();
      if (!place?.address_components) return;

      const parsed = parsePlace(place);
      // Google puts the full formatted address in the input — override with just street
      if (inputRef.current) {
        inputRef.current.value = parsed.addressLine1;
      }
      onChangeRef.current(parsed.addressLine1);
      onSelectRef.current(parsed);
    });

    autocompleteRef.current = autocomplete;

    return () => {
      window.google?.maps?.event.clearInstanceListeners(autocomplete);
    };
  }, [loaded]);

  // Sync the input's DOM value when React state changes (e.g. form reset)
  useEffect(() => {
    if (inputRef.current && inputRef.current !== document.activeElement) {
      inputRef.current.value = value;
    }
  }, [value]);

  return (
    <input
      ref={inputRef}
      type="text"
      defaultValue={value}
      onChange={(e) => onChange(e.target.value)}
      placeholder={placeholder}
      className={className}
      autoComplete="off"
    />
  );
}
